<script lang="ts" setup>
const props = defineProps<{
  total: number
  currentIndex: number
  to: (index: number) => void
  maxNumberOfDots?: number
  dotColor: 'white' | 'hos-blue'
}>()

const numberOfDotsDisplayedInTheMovingDirection = 2

const indexHistory = computed<{ current: number, previous: number }>(previous => ({ current: props.currentIndex, previous: previous?.current ?? 0 }))

const displayedIndexes = computed(() => {
  if (!props.maxNumberOfDots) {
    return {
      min: 0,
      max: props.total - 1,
    }
  }

  const { previous, current } = indexHistory.value

  let min: number
  let max: number

  const isMovingRight = current >= previous

  if (isMovingRight) {
    max = current + numberOfDotsDisplayedInTheMovingDirection
    min = max - props.maxNumberOfDots + 1
  } else {
    min = current - numberOfDotsDisplayedInTheMovingDirection
    max = min + props.maxNumberOfDots - 1
  }

  if (max < props.maxNumberOfDots) {
    min = 0
    max = props.maxNumberOfDots - 1
  } else if (max >= props.total - 1) {
    max = props.total - 1
    min = props.total - props.maxNumberOfDots
  }

  return { min, max }
})

function isSecondToLastDotButNotYetTheEnd(zeroBasedIndex: number) {
  return (zeroBasedIndex === displayedIndexes.value.min + 1 && zeroBasedIndex > 1) || (zeroBasedIndex === displayedIndexes.value.max - 1 && zeroBasedIndex < props.total - 2)
}

function isLastDotButNotYetTheEnd(zeroBasedIndex: number) {
  return (zeroBasedIndex === displayedIndexes.value.min && zeroBasedIndex > 0) || (zeroBasedIndex === displayedIndexes.value.max && zeroBasedIndex < props.total - 1)
}
</script>

<template>
  <ul class="absolute flex items-center bottom-5 left-5 gap-2">
    <li
      v-for="index of total"
      :key="index"
      class="w-3 h-1 rounded-full cursor-pointer transition-[width,background-color]"
      :class="{
        'bg-hos-gray bg-opacity-50': currentIndex !== index - 1,
        'hidden': index - 1 < displayedIndexes.min || index - 1 > displayedIndexes.max,
        '!w-5 bg-hos-blue': currentIndex === index - 1 && dotColor === 'hos-blue',
        '!w-5 bg-white': currentIndex === index - 1 && dotColor === 'white',
        '!w-2': isSecondToLastDotButNotYetTheEnd(index - 1),
        '!w-1 !h-0.5': isLastDotButNotYetTheEnd(index - 1),
      }"
      @click="props.to(index - 1)"
    />
  </ul>
</template>
